import waitFor from '../helpers/waitfor';
import amaLog, { amaAlert } from '../helpers/log';
import loadScript from './loadScript';
import { AdsConfigJson } from '../config/platform';
import { loadConfig } from '../config/platform';
import { getModules } from '../modules/index';
import { isVideoPage } from '../config/domain';
import { AD_PLACEMENT_ATTRIBUTE } from '../placement/placement';
import { pbjs } from '../prebid/index';
import { timeFor } from '../helpers/time';
import initGpt from '../googletag/googletag';
import { perfEventCollector } from '../adstats/perfEvent';

let demandManagerLoaded = false;
let gptLoaded = false;
let adnLoaded = false;
const adPlacementSelector = `div[${AD_PLACEMENT_ATTRIBUTE}]`;

export const resourceTimeout = 20000;

const isDemandManagerLoaded = () => demandManagerLoaded;
const isGptLoaded = () => gptLoaded;


export const isAnyPlacementsLoaded = () => !!document.querySelector<HTMLElement>(adPlacementSelector);

export const loadTopAdPlacement = async (topAdTarget: string) => {

  const { useLoadTopad } = getModules();

  if (useLoadTopad) {

    amaLog('loadTopAdPlacement ...', topAdTarget);

    // ToDo: Move top-ad placement handling to platform in se-ocelot  
    const isApp = window.location.search.includes('allerapp=1');
    if (!isApp) {

      let topAdElement = document.createElement('div');
      topAdElement.id = `${topAdTarget}`;
      topAdElement.setAttribute('data-aller-ad-placement', 'top_ad');

      await waitFor(() => typeof window?.document?.body?.insertBefore !== 'undefined', { timeout: 5000, errorMessage: `Timeout: window.document.body missing. top_ad is not injected` }).then(() => {

        window.document.body.insertBefore(topAdElement, window.document.body.firstChild);
      });
    }
  }
}

export const loadGpt = async () => {

  const { useLoadGptByAdsLib } = getModules();

  if (useLoadGptByAdsLib) {

    amaLog(`Start load GPT ${timeFor('loadGptstart')}`);

    try {
      loadScript("https://securepubads.g.doubleclick.net/tag/js/gpt.js", {
        async: true,
        defer: false,
        referrerpolicy: "strict-origin-when-cross-origin"
      }).then(() => {
        gptLoaded = true;
        amaLog('GptLoaded');
      });
    } catch (error) {
      console.error('Gpt failed to Load', error);
      gptLoaded = true;
    }
  } else {
    amaLog('Gpt not loaded by ads-lib');
    gptLoaded = true;
  }
};


export const loadDemandManager = async (accPrebidUrl: string) => {

  const { prebidScript, usePlacementCheck } = getModules();

  amaLog(`Start load DM ${timeFor('loadDMstart')}`);

  amaLog(`DemandManager loading by ads-lib ...`);

  amaLog('usePlacementCheck', usePlacementCheck);

  if (usePlacementCheck && !isVideoPage()) {

    if (!isAnyPlacementsLoaded()) {
      amaLog(`Ads:: Wait for Placement Wrapper to be loaded ...`);
      await waitFor(isAnyPlacementsLoaded, {
        timeout: 20000,
        errorMessage: `Timeout: Placementwrappers are missing `,
      });
    }
    amaLog('Ads:: Placement Wrapper is loaded');
  }
  const prebidUrl = prebidScript ?? accPrebidUrl;
  amaLog("prebidUrl", prebidUrl);
  try {
    loadScript(prebidUrl, {
      async: true,
      defer: false,
      referrerpolicy: "strict-origin-when-cross-origin"
    }).then(() => {
      demandManagerLoaded = true;
      amaLog('DemandManagerLoaded');
    });
  } catch (error) {
    console.error('DemandManager failed to Load', error);
    demandManagerLoaded = true;
  }
};

let loadingGptPending = false;

export const handleGpt = async (loadedByAdsLib: LoadedByAdsLib) => {

  amaLog(`handleGpt ${timeFor('loadGpt')}`);

  if (loadedByAdsLib?.gpt) {

    if (!loadingGptPending) {
      loadingGptPending = true;
      loadGpt();
    }

    await waitFor(isGptLoaded, {
      timeout: 10000,
      errorMessage: `Timeout: Waiting for gpt to be loaded `
    });
    amaLog(`GPT loaded ${timeFor('loadGpt')}`);
  }

  await initGpt();
}


let loadingDmPending = false;
let bidRequestedListenerAttached = false;

export const handleDemandManager = async (prebidUrl: string) => {

  const { useLoadDmByAdsLib } = getModules();

  amaLog(`handleDemandManager ${timeFor('loadDM')}`);

  pbjs.que.push(() => {
    demandManagerLoaded = true;
    amaLog(`prebid pbjs.que ready ${timeFor('prebidPbjsQueReady')}`);
  });

  if (useLoadDmByAdsLib && !loadingDmPending) {
    loadingDmPending = true;
    loadDemandManager(prebidUrl);
  } else {
    amaLog('DemandManager is loaded from platform bundle, not by ads-lib');
  }

  await waitFor(isDemandManagerLoaded, {
    timeout: 10000,
    errorMessage: `Timeout: Waiting for dm to be loaded `
  });
  amaLog(`DM loaded ${timeFor('loadDM')}`);

  if (!bidRequestedListenerAttached) {
    bidRequestedListenerAttached = true;

    pbjs.que = pbjs.que || [];
    pbjs.que.push(() => {
      pbjs.onEvent?.('bidRequested', (bidData: any) => {

        const consent = bidData.gdprConsent;
        const bidder = `${bidData.bidderCode}`;
        if (!(consent?.gdprApplies && typeof consent?.consentString !== 'undefined')) {
          amaAlert(`prebid pbjs.onEvent(bidRequested) ${bidder} ${timeFor(bidder)} - Consent missing!  `, bidData);
          // Track consent missing in bid for Grafana telemetry
          perfEventCollector.incrementConsentMissingInBid();
        }
      });
    });
  }

}
export const loadAdnScript = async (usePoliticalAds: boolean): Promise<void> => {

  if (usePoliticalAds) {

    amaLog(`Start load Political ADS script ${timeFor('loadADNstart')}`);
    try {
      await loadScript('https://cdn.adnuntius.com/adn.js', {
        async: true,
        defer: false,
        referrerpolicy: "strict-origin-when-cross-origin"
      });
      adnLoaded = true;
      amaLog('AdnLoaded');
    } catch (error) {
      console.error('Adn failed to Load', error);
      adnLoaded = true;
    }
  } else {
    amaLog('Adn not loaded by ads-lib');
    adnLoaded = true;
  }
};


export const initAdResources = async (parsedConfig: AdsConfigJson) => {

  amaLog('initAdResources ...');
  // Load platformConfig if available

  const accConfig = await loadConfig(parsedConfig);

  return accConfig;
};

export const tcfApiLoaded = async () => {

  amaLog('waitFor tcfApiLoaded ...');

  return await waitFor(() => typeof window?.__tcfapi !== 'undefined', { timeout: resourceTimeout, errorMessage: `Timeout: window.__tcfapi not available ` }).then(() => {
    amaLog('tcfApi is loaded');
  });
};

